import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonIcon,
  Modal,
  Toggle,
} from '@rabbit/elements/shared-components';
import {
  nestApiCreateMainGroup,
  nestApiUpdateMainGroup,
  nestApiDisableMainGroup,
  nestApiEnableMainGroup,
  nestApiGetAllMainGroups,
} from '@rabbit/bizproc/core';
import { DTMainGroup } from '@rabbit/data/types';
import { Input } from '@rabbit/elements/shared-components';
import { useEffect, useMemo, useState, useRef } from 'react';
import { CreateTable } from '@rabbit/sage/utils/CreateTable';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { MainGroupCreateDto, MainGroupUpdateDto } from '@rabbit/nest/api';

interface MainGroupListShape {
  docid: string;
  id: string;
  value: string;
  label: string;
  name: string;
  description: string;
  sharedLocation: boolean;
}

export default function MainGroupTab() {
  const { t } = useTranslation();
  const formRef = useRef(null) as any;
  const [isLoading, setIsLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedMainGroup, setSelectedMainGroup] =
    useState<DTMainGroup | null>(null);
  const [mainGroupList, setMainGroupList] = useState<DTMainGroup[]>([]);

  useEffect(() => {
    const fetchMainGroups = async () => {
      try {
        const response = await nestApiGetAllMainGroups();
        if (response) {
          setMainGroupList(response as any);
        }
      } catch (error) {
        console.error('Failed to fetch main groups:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMainGroups();
  }, []);

  const modalSettings = {
    kind: 'generic' as const,
    settings: {
      title: selectedMainGroup
        ? t('general.editMainGroup')
        : t('general.addMainGroup'),
      text: selectedMainGroup
        ? t('general.editMainGroup')
        : t('general.addMainGroup'),
      handleClose: () => {
        setEditModalOpen(false);
        setSelectedMainGroup(null);
      },
    },
  };

  const deleteModalSettings = {
    kind: 'generic' as const,
    settings: {
      title: t('Delete Main Group'),
      text: t('Are you sure you want to delete this main group?'),
      primaryButtonText: t('general.delete'),
      outlineButtonText: t('general.cancel'),
      handleClose: () => {
        setDeleteModalOpen(false);
        setSelectedMainGroup(null);
      },
      handlePrimaryClick: async () => {
        if (selectedMainGroup) {
          await handleDelete(selectedMainGroup.docid);
          setDeleteModalOpen(false);
          setSelectedMainGroup(null);
        }
      },
    },
  };

  const schema = Yup.object({
    title: Yup.string().required(t('message.pleaseEnterAName')),
    description: Yup.string().optional(),
    sharedLocation: Yup.boolean(),
  });

  const initialValues = {
    title: selectedMainGroup?.title || '',
    description: selectedMainGroup?.description || '',
    sharedLocation: selectedMainGroup?.sharedLocation ?? false,
  };

  const handleSubmit = async (values: {
    title: string;
    description: string;
    sharedLocation: boolean;
  }) => {
    try {
      if (selectedMainGroup) {
        await handleUpdate(selectedMainGroup.docid, {
          input: {
            title: values.title,
            description: values.description,
            sharedLocation: values.sharedLocation,
          },
        });
      } else {
        await handleCreate({
          tenantLink: t('tenantLink'),
          input: {
            title: values.title,
            description: values.description,
            sharedLocation: values.sharedLocation,
          },
        });
      }
    } catch (error) {
      console.error('Failed to submit form:', error);
    }
  };

  const handleEditModal = (itemDetails?: MainGroupListShape) => {
    if (itemDetails) {
      setSelectedMainGroup(
        mainGroupList.find((g) => g.docid === itemDetails.docid) || null
      );
    } else {
      setSelectedMainGroup(null);
    }
    setEditModalOpen(true);
  };

  const handleCreate = async (data: MainGroupCreateDto) => {
    try {
      const response = await nestApiCreateMainGroup(data);
      if (response) {
        setMainGroupList((prevList) => [...prevList, response as any]);
      }
      setEditModalOpen(false);
    } catch (error) {
      console.error('Failed to create main group:', error);
    }
  };

  const handleUpdate = async (docid: string, data: MainGroupUpdateDto) => {
    try {
      const response = await nestApiUpdateMainGroup({ docid, ...data });
      if (response) {
        setMainGroupList((prevList) =>
          prevList.map((item) =>
            item.docid === docid ? (response as any) : item
          )
        );
      }
      setEditModalOpen(false);
    } catch (error) {
      console.error('Failed to update main group:', error);
    }
  };

  const handleDelete = async (docid: string) => {
    try {
      const response = await nestApiDisableMainGroup({ docid });
      if (response) {
        setMainGroupList((prevList) =>
          prevList.filter((item) => item.docid !== docid)
        );
      }
    } catch (error) {
      console.error('Failed to delete main group:', error);
    }
  };

  const handleToggle = async (docid: string, sharedLocation: boolean) => {
    try {
      const response = sharedLocation
        ? await nestApiEnableMainGroup({ docid })
        : await nestApiDisableMainGroup({ docid });

      if (response) {
        setMainGroupList((prevList) =>
          prevList.map((item) =>
            item.docid === docid ? (response as any) : item
          )
        );
      }
    } catch (error) {
      console.error('Failed to toggle main group:', error);
    }
  };

  const MainGroupTable = useMemo(() => {
    const mainGroupListOptions: MainGroupListShape[] =
      mainGroupList?.map((group) => ({
        docid: group.docid,
        id: group.docid,
        value: group.docid,
        label: group.title,
        name: group.title,
        description: group.description || '-',
        sharedLocation: group.sharedLocation,
      })) || [];
    console.log('mainGroupListOptions', mainGroupListOptions, mainGroupList);

    return CreateTable<MainGroupListShape>(mainGroupListOptions, [
      {
        header: t('general.name'),
        accessorKey: 'label',
        value: (group) => group.label,
      },
      {
        header: t('general.description'),
        accessorKey: 'description',
        value: (group) => group.description,
      },
      {
        header: t('general.sharedLocation'),
        accessorKey: 'sharedLocation',
        value: (group) =>
          group.sharedLocation ? t('general.yes') : t('general.no'),
      },
      {
        header: '',
        accessorKey: 'actions',
        value: (group) => (
          <div className="text-center">
            <ButtonIcon
              label={t('general.editMainGroup')}
              kind={'bgLightGreen'}
              iconLeft={true}
              Icon={PencilSquareIcon}
              onClick={() => {
                setSelectedMainGroup(
                  mainGroupList.find((g) => g.docid === group.docid) || null
                );
                handleEditModal(group);
              }}
            />
          </div>
        ),
        size: 15,
        maxSize: 30,
      },
    ]);
  }, [mainGroupList, isLoading, t]);

  return (
    <>
      <div className="relative z-10 flex justify-end py-4">
        <div className="md:absolute md:right-5 md:top-[76px] xl:top-[60px]">
          <ButtonIcon
            label={t('general.addMainGroup')}
            iconLeft={true}
            onClick={() => handleEditModal()}
          />
        </div>
      </div>
      <MainGroupTable
        initialState={{
          columnPinning: { right: ['actions'] },
          showGlobalFilter: true,
          isLoading,
        }}
        muiSearchTextFieldProps={{
          placeholder: t('general.searchNameOrDescription'),
          sx: {
            minWidth: '600px',
            borderRadius: '20px',
            backgroundColor: '#fff',
            margin: '5px 0',
            '& .MuiOutlinedInput-root': {
              '& > fieldset': { borderRadius: '10px' },
            },
          },
          variant: 'outlined',
          margin: 'none',
        }}
      />
      {editModalOpen && (
        <Modal
          className="lg:w-[724px]"
          kind={'generic'}
          settings={modalSettings.settings}
        >
          <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={schema}
            validateOnChange={true}
            validateOnBlur={false}
            innerRef={formRef}
          >
            {(props) => (
              <Form className="mt-2 w-full px-5">
                <div className="w-full py-2">
                  <Input
                    type="text"
                    label={`${t('general.name')}*`}
                    name="title"
                    settings={{
                      id: 'title',
                      hint: '*required',
                      placeholder: t('general.mainGroupName'),
                    }}
                  />
                </div>
                <div className="w-full py-2">
                  <Input
                    type="textarea"
                    label={`${t('general.description')}*`}
                    name="description"
                    settings={{
                      id: 'description',
                      hint: '*required',
                      placeholder: t('general.description'),
                    }}
                  />
                </div>

                {/* {selectedMainGroup && ( */}
                <div className="mt-5 flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2">
                  <div className="grow">{t('general.sharedLocation')}</div>
                  <Toggle
                    name="sharedLocation"
                    checked={props.values?.sharedLocation ?? false}
                    onChange={() => {
                      props.setFieldValue(
                        'sharedLocation',
                        !props.values.sharedLocation
                      );
                    }}
                  />
                </div>
                {/* )} */}

                <div className="mt-8 flex gap-5 py-2">
                  <Button
                    kind={'primary'}
                    type="submit"
                    disabled={props.isSubmitting || !props.isValid}
                  >
                    {t('general.save')}
                  </Button>
                  <Button kind={'red'} onClick={() => setEditModalOpen(false)}>
                    {t('general.cancel')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal>
      )}
      {deleteModalOpen && (
        <Modal
          className="h-fit rounded-md bg-white lg:w-[724px]"
          kind={'pop-up'}
          settings={deleteModalSettings.settings}
        />
      )}
    </>
  );
}
